import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Input, Select, Sheet, Table } from '../components/ui';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { listDevices, type DeviceRecord } from '../api/devices';
import {
  deleteSubmission,
  deleteSubmissions,
  hasConsent,
  hasPhoto,
  listSubmissions,
  submissionSummary,
  subscribeSubmissions,
  toCsv,
  type SubmissionRecord
} from '../api/submissions';

function relativeTime(iso: string | undefined): string {
  if (!iso) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function Submissions() {
  const { session } = useSession();
  const { notify } = useToast();
  const role = session?.role ?? 'viewer';
  const canDelete = role === 'owner' || role === 'admin';
  const canSeePhoto = role === 'owner' || role === 'admin';

  const [rows, setRows] = useState<SubmissionRecord[]>([]);
  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [selected, setSelected] = useState<SubmissionRecord | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await listSubmissions({ query, from, to, device: deviceFilter }));
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load submissions.');
    } finally {
      setLoading(false);
    }
  }, [query, from, to, deviceFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => subscribeSubmissions(() => void load()), [load]);

  useEffect(() => {
    void listDevices()
      .then(setDevices)
      .catch(() => {});
  }, []);

  const toggle = (id: string) =>
    setSelectedIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  function exportCsv() {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / SUBMISSIONS</span>
          <h1>Submissions</h1>
        </div>
        <Button variant="quiet" type="button" onClick={exportCsv} disabled={rows.length === 0}>
          Export CSV
        </Button>
      </header>

      <div className="toolbar">
        <Input
          aria-label="Search submissions"
          placeholder="Search payload"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Input type="date" aria-label="From date" value={from} onChange={(event) => setFrom(event.target.value)} />
        <Input type="date" aria-label="To date" value={to} onChange={(event) => setTo(event.target.value)} />
        <Select aria-label="Filter by device" value={deviceFilter} onChange={(event) => setDeviceFilter(event.target.value)}>
          <option value="">All devices</option>
          {devices.map((device) => (
            <option key={device.id} value={device.id}>
              {device.name ?? device.id}
            </option>
          ))}
        </Select>
      </div>

      {canDelete && selectedIds.length > 0 && (
        <div className="bulk-bar">
          <span>{selectedIds.length} selected</span>
          <Button variant="danger" type="button" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        </div>
      )}

      {loading && <LoadingState label="Loading submissions" />}
      {error && <ErrorState title="Submissions unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && rows.length === 0 && (
        <EmptyStateBlock title="No submissions yet" detail="Check-in submissions appear here in real time." />
      )}

      {!loading && !error && rows.length > 0 && (
        <section className="panel">
          <Table>
            <thead>
              <tr>
                {canDelete && <th aria-label="Select" />}
                <th>Time</th>
                <th>Summary</th>
                <th>Device</th>
                <th>Consent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="row-clickable" onClick={() => setSelected(row)}>
                  {canDelete && (
                    <td onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label="Select submission"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggle(row.id)}
                      />
                    </td>
                  )}
                  <td className="mono">{relativeTime(row.submitted_at)}</td>
                  <td>{submissionSummary(row)}</td>
                  <td>{row.expand?.device?.name ?? row.device ?? '—'}</td>
                  <td>
                    <Badge>{hasConsent(row) ? 'consented' : 'none'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}

      {selected && (
        <Sheet open title="Submission" onClose={() => setSelected(null)}>
          <dl className="kv">
            <dt>Time</dt>
            <dd>{selected.submitted_at ?? '—'}</dd>
            <dt>Client id</dt>
            <dd className="mono">{selected.client_id ?? '—'}</dd>
            <dt>Device</dt>
            <dd>{selected.expand?.device?.name ?? selected.device ?? '—'}</dd>
            <dt>Layout</dt>
            <dd>{selected.expand?.layout?.name ?? selected.layout ?? '—'}</dd>
            <dt>Consent</dt>
            <dd>{hasConsent(selected) ? 'given' : 'not recorded'}</dd>
            <dt>Photo</dt>
            <dd>{hasPhoto(selected) ? (canSeePhoto ? 'present' : 'restricted to admin+') : 'none'}</dd>
          </dl>
          <section className="detail-section">
            <span className="index-label">PAYLOAD</span>
            <pre className="payload-view">
              {JSON.stringify(canSeePhoto ? selected.payload : redactPhoto(selected.payload), null, 2)}
            </pre>
          </section>
          {canDelete && (
            <div className="detail-danger">
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  void deleteSubmission(selected.id).then(() => {
                    notify('Submission deleted');
                    setSelected(null);
                    void load();
                  });
                }}
              >
                Delete submission
              </Button>
            </div>
          )}
        </Sheet>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete submissions"
        confirmWord="delete"
        detail={`${selectedIds.length} submission(s) will be permanently removed (erasure request).`}
        confirmLabel="Delete"
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          void deleteSubmissions(selectedIds).then(() => {
            notify(`${selectedIds.length} submission(s) deleted`);
            setSelectedIds([]);
            void load();
          });
        }}
      />
    </main>
  );
}

/** Strip photo fields for non-admin viewers before rendering the payload. */
function redactPhoto(payload: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!payload) return {};
  const copy = { ...payload };
  delete copy.photo;
  delete copy.photo_url;
  return copy;
}
